import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { t } from "@/i18n";
import { toast } from "sonner";
import { Plus, Globe, Footprints, PhoneCall, UserPlus, Crown, X, ChevronDown, AlertTriangle } from "lucide-react";

/* =================================================================
   SONARA — Reservations
   Spreadsheet rows have been replaced by Living Guest Cards.
   Refer to /app/design_guidelines.json (Phase 1 redesign).
================================================================= */

const STATUS_META = {
  pending:        { dotClass: "status-dot--pending",        label: "Ausstehend" },
  confirmed:      { dotClass: "status-dot--confirmed",      label: "Bestätigt" },
  arrived:        { dotClass: "status-dot--arrived",        label: "Eingetroffen" },
  waiting_lounge: { dotClass: "status-dot--waiting_lounge", label: "Lounge" },
  table_ready:    { dotClass: "status-dot--table_ready",    label: "Tisch bereit" },
  seated:         { dotClass: "status-dot--seated",         label: "Platziert" },
  completed:      { dotClass: "status-dot--completed",      label: "Abgeschlossen" },
  cancelled:      { dotClass: "status-dot--cancelled",      label: "Storniert" },
  no_show:        { dotClass: "status-dot--no_show",        label: "No-Show" },
};

const ACTIVE_STATUSES = new Set(["arrived", "waiting_lounge", "table_ready", "seated"]);

const SOURCE_META = {
  online:  { Icon: Globe,     label: "Online"    },
  manual:  { Icon: UserPlus,  label: "Manuell"   },
  phone:   { Icon: PhoneCall, label: "Telefon"   },
  walk_in: { Icon: Footprints,label: "Walk-in"   },
  vip:     { Icon: Crown,     label: "VIP"       },
};

const GROUP_BY_OPTIONS = [
  { key: "room",         labelKey: "reservations.group_by_options.room" },
  { key: "table_waiter", labelKey: "reservations.group_by_options.table_waiter" },
];

export default function Reservations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [filter, setFilter] = useState("");
  const [groupBy, setGroupBy] = useState("room");
  const [rooms, setRooms] = useState([]);
  const [tables, setTables] = useState([]);
  const [staff, setStaff] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const load = async () => {
    try {
      const params = { date };
      if (filter) params.status = filter;
      const { data } = await api.get("/reservations", { params });
      setItems(data);
    } catch {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, filter]);
  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/tables").then((r) => setTables(r.data)).catch(() => {});
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, []);

  const updateStatus = async (id, status, e) => {
    e?.stopPropagation();
    try {
      await api.patch(`/reservations/${id}`, { status });
      toast.success(t(`reservations.statuses.${status}`));
      load();
    } catch {
      toast.error("Update fehlgeschlagen");
    }
  };

  const lookupRoomName = (rid) => rooms.find((r) => r.room_id === rid)?.name;
  const lookupTableRoom = (tid) => {
    const tbl = tables.find((x) => x.table_id === tid);
    if (!tbl) return null;
    return rooms.find((r) => r.room_id === tbl.room_id)?.name || tbl.name;
  };
  const lookupTableName = (tid) => tables.find((x) => x.table_id === tid)?.name;
  const lookupWaiterName = (uid) => staff.find((s) => s.user_id === uid)?.name;

  const grouped = useMemo(() => {
    const groups = new Map();
    const sortFn = (a, b) => (a.time || "").localeCompare(b.time || "");
    for (const r of items) {
      let key, label;
      if (groupBy === "room") {
        const name = lookupRoomName(r.preferred_room_id) || (r.table_id ? lookupTableRoom(r.table_id) : null);
        if (name) { key = `room:${name}`; label = name; }
        else { key = "room:none"; label = t("reservations.no_group"); }
      } else {
        const name = lookupWaiterName(r.assigned_waiter_id);
        if (name) { key = `wtr:${r.assigned_waiter_id}`; label = name; }
        else { key = "wtr:none"; label = t("reservations.no_waiter"); }
      }
      if (!groups.has(key)) groups.set(key, { key, label, items: [] });
      groups.get(key).items.push(r);
    }
    const list = Array.from(groups.values()).map((g) => ({ ...g, items: g.items.sort(sortFn) }));
    list.sort((a, b) => (a.items[0]?.time || "").localeCompare(b.items[0]?.time || ""));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, groupBy, rooms, tables, staff]);

  const toggleGroup = (key) => setCollapsedGroups((g) => ({ ...g, [key]: !g[key] }));

  return (
    <div className="px-6 lg:px-12 py-12 max-w-6xl mx-auto">
      {/* ===== Editorial header ===== */}
      <header className="mb-12 animate-fade-up">
        <h1
          className="font-editorial italic text-5xl sm:text-6xl leading-[1.05] text-[var(--text-primary)] tracking-tight"
          data-testid="reservations-title"
        >
          {t("reservations.title")}
        </h1>
        <p className="text-[var(--text-muted)] text-sm mt-3 max-w-xl font-light">
          Jede Buchung ist ein Moment. Sehen Sie Ihre Gäste auf einen Blick — wer kommt, wer wartet, wer bereits am Tisch sitzt.
        </p>
      </header>

      {/* ===== Toolbar — minimal, editorial ===== */}
      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-5 mb-10 animate-fade-up-delay-1">
        <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
          <ToolbarField label="Datum">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="reservations-date-filter"
              className="linen-input min-w-[160px] [color-scheme:dark]"
            />
          </ToolbarField>
          <ToolbarField label="Status">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="reservations-status-filter"
              className="linen-input min-w-[140px] cursor-pointer"
            >
              <option value="">Alle</option>
              {Object.keys(STATUS_META).map((s) => (
                <option key={s} value={s}>{t(`reservations.statuses.${s}`)}</option>
              ))}
            </select>
          </ToolbarField>
          <ToolbarField label={t("reservations.group_by")}>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              data-testid="reservations-group-by"
              className="linen-input min-w-[160px] cursor-pointer"
            >
              {GROUP_BY_OPTIONS.map((g) => (
                <option key={g.key} value={g.key}>{t(g.labelKey)}</option>
              ))}
            </select>
          </ToolbarField>
        </div>
        <button
          onClick={() => setShowManual(true)}
          data-testid="reservations-add-manual-btn"
          className="btn-primary text-[11px] tracking-[0.18em] uppercase inline-flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.6} /> {t("reservations.add_manual")}
        </button>
      </div>

      {/* ===== Groups & living cards ===== */}
      {items.length === 0 ? (
        <div className="py-20 text-center animate-fade-up-delay-2" data-testid="reservations-empty">
          <div className="font-editorial italic text-3xl text-[var(--text-muted)]">
            Heute Abend ist es ruhig.
          </div>
          <div className="text-xs text-[var(--text-dim)] mt-2 tracking-wide">
            Noch keine Buchungen — geniessen Sie die Stille.
          </div>
        </div>
      ) : (
        <div className="space-y-12 animate-fade-up-delay-2" data-testid="reservations-groups">
          {grouped.map((group) => {
            const collapsed = collapsedGroups[group.key];
            return (
              <section key={group.key} data-testid={`group-${group.key}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  className="group w-full flex items-baseline justify-between gap-4 mb-6 text-left"
                  data-testid={`group-header-${group.key}`}
                >
                  <div className="flex items-baseline gap-4 min-w-0">
                    <h2 className="font-editorial italic text-[2rem] leading-none text-[var(--text-primary)] tracking-tight">
                      {group.label}
                    </h2>
                    <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--gold-warm)]">
                      {group.items.length} {group.items.length === 1 ? "Gast" : "Gäste"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span aria-hidden className="hidden sm:block w-24 h-px brass-line opacity-70 group-hover:opacity-100 transition-opacity duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-[var(--text-dim)] transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${collapsed ? "-rotate-90" : ""}`}
                      strokeWidth={1.4}
                    />
                  </div>
                </button>

                {!collapsed && (
                  <div className="space-y-3">
                    {group.items.map((r) => (
                      <GuestCard
                        key={r.reservation_id}
                        reservation={r}
                        onOpen={() => navigate(`/dashboard/reservations/${r.reservation_id}`)}
                        onStatus={updateStatus}
                        tableLabel={lookupTableName(r.table_id)}
                        roomLabel={lookupRoomName(r.preferred_room_id) || (r.table_id ? lookupTableRoom(r.table_id) : null)}
                        waiterLabel={lookupWaiterName(r.assigned_waiter_id)}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {showManual && (
        <ManualReservationModal
          onClose={() => setShowManual(false)}
          onCreated={() => { setShowManual(false); load(); }}
          tables={tables}
          rooms={rooms}
          staff={staff}
          date={date}
        />
      )}
    </div>
  );
}

/* ===================================================================
   GuestCard — a living moment, not a row.
=================================================================== */
function GuestCard({ reservation: r, onOpen, onStatus, tableLabel, roomLabel, waiterLabel }) {
  const SrcMeta = SOURCE_META[r.source] || SOURCE_META.online;
  const SourceIcon = SrcMeta.Icon;
  const meta = STATUS_META[r.status] || STATUS_META.pending;
  const isActive = ACTIVE_STATUSES.has(r.status);
  const isTerminal = ["cancelled", "completed", "no_show"].includes(r.status);
  const initial = (r.guest_name || "?")[0]?.toUpperCase();
  const allergies = r.allergies || [];

  return (
    <article
      onClick={onOpen}
      data-testid={`reservation-${r.reservation_id}`}
      className={`lift-luxury group relative cursor-pointer rounded-md px-6 sm:px-8 py-6 bg-[#171410] hover:bg-[#1B1812] ${isTerminal ? "opacity-65" : ""}`}
      style={{ boxShadow: "0 1px 0 rgba(255,255,255,0.02) inset, 0 18px 60px -36px rgba(0,0,0,0.5)" }}
    >
      {/* faint brass top hairline */}
      <span aria-hidden className="absolute left-6 right-6 top-0 h-px brass-line opacity-50 group-hover:opacity-90 transition-opacity duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />

      <div className="flex items-center gap-5 sm:gap-7">
        {/* ===== Time + source — cinematic editorial number ===== */}
        <div className="flex flex-col items-center w-[68px] sm:w-[80px] shrink-0">
          <div className="font-editorial text-[2.1rem] sm:text-[2.4rem] leading-none text-[var(--text-primary)] tracking-tight">
            {r.time}
          </div>
          <div
            className="mt-2 inline-flex items-center gap-1 text-[10.5px] tracking-[0.22em] uppercase text-[var(--text-muted)] font-medium"
            data-testid={`reservation-source-${r.reservation_id}`}
            title={SrcMeta.label}
          >
            <SourceIcon className="w-3 h-3 text-[var(--gold-warm)]" strokeWidth={1.4} />
            <span>{SrcMeta.label}</span>
          </div>
        </div>

        {/* divider — vertical brass hairline */}
        <span aria-hidden className="self-stretch w-px brass-line-v opacity-50" />

        {/* ===== Avatar + Name + meta ===== */}
        <div className="flex items-center gap-4 sm:gap-5 min-w-0 flex-1">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#1D1813] amber-halo flex items-center justify-center shrink-0">
            <span className="font-editorial text-[1.3rem] text-[var(--gold-cool)] leading-none">{initial}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-editorial text-[1.55rem] sm:text-[1.7rem] leading-tight text-[var(--text-primary)] truncate">
              {r.guest_name}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--text-muted)] tracking-wide">
              <span>{r.party_size} {r.party_size === 1 ? "Gast" : "Gäste"}</span>
              <span className="text-[var(--text-dim)]">·</span>
              <span>{t(`reservations.types.${r.reservation_type}`) || r.reservation_type}</span>
              {roomLabel && (<><span className="text-[var(--text-dim)]">·</span><span>{roomLabel}{tableLabel ? ` · ${tableLabel}` : ""}</span></>)}
              {waiterLabel && (<><span className="text-[var(--text-dim)]">·</span><span className="italic">{waiterLabel}</span></>)}
            </div>
            {allergies.length > 0 && (
              <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#D89A9A] tracking-wider">
                <AlertTriangle className="w-3 h-3" strokeWidth={1.5} />
                <span>{allergies.slice(0, 3).join(" · ")}</span>
              </div>
            )}
          </div>
        </div>

        {/* ===== Status + actions (right) ===== */}
        <div className="flex flex-col items-end gap-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hidden sm:inline">
              {t(`reservations.statuses.${r.status}`)}
            </span>
            <span
              className={`status-dot ${meta.dotClass} ${isActive ? "pulse-live" : ""}`}
              data-testid={`status-dot-${r.reservation_id}`}
            />
          </div>

          {/* Quick actions — hidden by default, revealed on hover */}
          <div
            className="hidden md:flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            onClick={(e) => e.stopPropagation()}
          >
            <QuietAction visible={r.status === "pending"} onClick={(e) => onStatus(r.reservation_id, "confirmed", e)} testid={`confirm-${r.reservation_id}`} variant="gold">Bestätigen</QuietAction>
            <QuietAction visible={r.status === "pending" || r.status === "confirmed"} onClick={(e) => onStatus(r.reservation_id, "waiting_lounge", e)} testid={`lounge-${r.reservation_id}`}>In Lounge</QuietAction>
            <QuietAction visible={["pending","confirmed","arrived","waiting_lounge","table_ready"].includes(r.status)} onClick={(e) => onStatus(r.reservation_id, "seated", e)} testid={`seat-${r.reservation_id}`} variant="gold">Platzieren</QuietAction>
            <QuietAction visible={r.status === "seated"} onClick={(e) => onStatus(r.reservation_id, "completed", e)} testid={`complete-${r.reservation_id}`}>Abschliessen</QuietAction>
            <QuietAction visible={!["cancelled","completed","no_show"].includes(r.status)} onClick={(e) => onStatus(r.reservation_id, "cancelled", e)} testid={`cancel-${r.reservation_id}`} variant="muted">Stornieren</QuietAction>
          </div>
        </div>
      </div>

      {r.notes && (
        <div className="mt-4 pl-[100px] sm:pl-[112px] text-[12px] italic text-[var(--text-muted)] font-editorial leading-relaxed">
          „{r.notes}"
        </div>
      )}
    </article>
  );
}

function QuietAction({ children, onClick, testid, visible, variant }) {
  if (!visible) return null;
  const colors = {
    gold:  "text-[var(--gold)] hover:text-[var(--gold-cool)]",
    muted: "text-[var(--text-dim)] hover:text-[#C97A7A]",
    default: "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative text-[10px] tracking-[0.22em] uppercase transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${colors[variant || "default"]}`}
    >
      <span className="gold-sweep">{children}</span>
    </button>
  );
}

function ToolbarField({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] font-medium">{label}</span>
      {children}
    </label>
  );
}

/* ===================================================================
   Manual reservation modal (linen inputs + editorial breath marks)
=================================================================== */
function ManualReservationModal({ onClose, onCreated, tables, rooms, staff, date }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    source: "walk_in",
    guest_first_name: "",
    guest_last_name: "",
    guest_email: "",
    guest_phone: "",
    party_size: 2,
    date,
    time: new Date().toTimeString().slice(0, 5),
    preferred_room_id: "",
    table_id: "",
    assigned_waiter_id: "",
    reservation_type: "dinner",
    notes: "",
    allergies: "",
    status: "confirmed",
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tablesForRoom = useMemo(() => {
    if (!form.preferred_room_id) return [];
    return tables.filter((tbl) => tbl.room_id === form.preferred_room_id && tbl.status === "free");
  }, [tables, form.preferred_room_id]);
  const setRoom = (room_id) => setForm((f) => ({ ...f, preferred_room_id: room_id, table_id: "" }));
  const waiters = useMemo(
    () => staff.filter((s) => ["waiter", "host"].includes(s.role)).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [staff]
  );

  const submit = async (e) => {
    e.preventDefault();
    const firstName = form.guest_first_name.trim();
    const lastName = form.guest_last_name.trim();
    if (!firstName || !lastName || !form.guest_phone || !form.party_size || !form.time || !form.preferred_room_id || !form.table_id) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reservations", {
        guest_name: `${firstName} ${lastName}`,
        guest_first_name: firstName,
        guest_last_name: lastName,
        guest_phone: form.guest_phone.trim(),
        guest_email: form.guest_email.trim(),
        party_size: parseInt(form.party_size, 10),
        date: form.date, time: form.time,
        reservation_type: form.reservation_type,
        notes: form.notes,
        allergies: form.allergies ? form.allergies.split(",").map((a) => a.trim()).filter(Boolean) : [],
        source: form.source, status: form.status,
        preferred_room_id: form.preferred_room_id,
        table_id: form.table_id,
        assigned_waiter_id: form.assigned_waiter_id || null,
      });
      toast.success(t("reservations.manual_modal.saved"));
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setSubmitting(false);
    }
  };

  const sources = [
    { key: "walk_in", icon: Footprints, label: t("reservations.sources.walk_in") },
    { key: "phone",   icon: PhoneCall,  label: t("reservations.sources.phone") },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/75 backdrop-blur-md p-4 sm:p-6 animate-fade-up"
      style={{ animationDuration: "260ms" }}
      onClick={onClose}
      data-testid="manual-reservation-modal"
    >
      <div
        className="relative w-full max-w-2xl my-auto bg-[#16120E] rounded-md flex flex-col overflow-hidden"
        style={{
          maxHeight: "min(92vh, 920px)",
          boxShadow: "0 1px 0 rgba(184,150,86,0.08) inset, 0 60px 120px -40px rgba(0,0,0,0.7)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span aria-hidden className="absolute top-0 left-10 right-10 h-px brass-line opacity-80 z-10" />

        {/* ===== Sticky header — Title + Source pills always visible ===== */}
        <header className="shrink-0 px-8 sm:px-10 pt-8 sm:pt-10 pb-6">
          <div className="flex items-start justify-between mb-7">
            <div className="min-w-0 pr-4">
              <h3 className="font-editorial italic text-[2rem] sm:text-[2.4rem] leading-[1.05] text-[var(--text-primary)] tracking-tight">
                {t("reservations.manual_modal.title")}
              </h3>
              <p className="text-[12.5px] text-[var(--text-muted)] mt-3 max-w-md font-light leading-relaxed">
                {t("reservations.manual_modal.subtitle")}
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="manual-modal-close"
              className="shrink-0 -mr-2 -mt-2 p-2 text-[var(--text-dim)] hover:text-[var(--text-primary)] transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            >
              <X className="w-5 h-5" strokeWidth={1.4} />
            </button>
          </div>

          {/* Source — always visible, never scrolled away */}
          <div>
            <div className="text-[10px] text-[var(--text-muted)] mb-3 uppercase tracking-[0.24em] font-medium">
              {t("reservations.manual_modal.source_label")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {sources.map((s) => {
                const active = form.source === s.key;
                return (
                  <button
                    key={s.key} type="button"
                    onClick={() => setField("source", s.key)}
                    data-testid={`manual-source-${s.key}`}
                    className={`px-4 py-3 rounded-sm border text-[11px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2 transition-colors duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      active
                        ? "border-[var(--gold)]/55 text-[var(--gold)] bg-[#18140F]"
                        : "border-white/[0.08] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-white/[0.16]"
                    }`}
                  >
                    <s.icon className="w-3.5 h-3.5" strokeWidth={1.4} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </header>

        {/* ===== Scrollable body ===== */}
        <form
          id="manual-reservation-form"
          onSubmit={submit}
          className="flex-1 overflow-y-auto px-8 sm:px-10 py-7 space-y-9 border-t border-white/[0.04]"
        >
          <ModalSection title="Gast" hint="Damit Ihr Team den Gast persönlich begrüssen kann.">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <LinenField label="Vorname *">
                <input required value={form.guest_first_name} onChange={(e) => setField("guest_first_name", e.target.value)} className="linen-input" data-testid="manual-guest-first-name" placeholder="Sofia" autoComplete="given-name" />
              </LinenField>
              <LinenField label="Nachname *">
                <input required value={form.guest_last_name} onChange={(e) => setField("guest_last_name", e.target.value)} className="linen-input" data-testid="manual-guest-last-name" placeholder="Bianchi" autoComplete="family-name" />
              </LinenField>
            </div>
          </ModalSection>

          <ModalSection title="Kontakt" hint="So erreicht Sie Ihr Team — diskret, falls etwas zu klären ist.">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <LinenField label="E-Mail">
                <input type="email" value={form.guest_email} onChange={(e) => setField("guest_email", e.target.value)} className="linen-input" data-testid="manual-guest-email" placeholder="sofia@example.com" autoComplete="email" />
              </LinenField>
              <LinenField label="Telefon *">
                <input required type="tel" value={form.guest_phone} onChange={(e) => setField("guest_phone", e.target.value)} className="linen-input" data-testid="manual-guest-phone" placeholder="+41 79 123 45 67" autoComplete="tel" />
              </LinenField>
            </div>
          </ModalSection>

          <ModalSection title="Zeit & Gäste">
            <div className="grid sm:grid-cols-3 gap-x-8 gap-y-6">
              <LinenField label={t("reservations.party") + " *"}>
                <input required type="number" min="1" max="40" value={form.party_size} onChange={(e) => setField("party_size", e.target.value)} className="linen-input" data-testid="manual-party-size" />
              </LinenField>
              <LinenField label={t("common.date") + " *"}>
                <input required type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="linen-input [color-scheme:dark]" data-testid="manual-date" />
              </LinenField>
              <LinenField label={t("common.time") + " *"}>
                <input required type="time" value={form.time} onChange={(e) => setField("time", e.target.value)} className="linen-input [color-scheme:dark]" data-testid="manual-time" />
              </LinenField>
            </div>
          </ModalSection>

          <ModalSection title="Platzierung">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <LinenField label={t("common.room") + " *"}>
                <select required value={form.preferred_room_id} onChange={(e) => setRoom(e.target.value)} className="linen-input cursor-pointer" data-testid="manual-room">
                  <option value="">Raum wählen —</option>
                  {rooms.map((r) => (<option key={r.room_id} value={r.room_id}>{r.name}</option>))}
                </select>
              </LinenField>
              <LinenField label={t("common.table") + " *"}>
                <select required disabled={!form.preferred_room_id} value={form.table_id} onChange={(e) => setField("table_id", e.target.value)}
                        className="linen-input cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" data-testid="manual-table">
                  <option value="">{form.preferred_room_id ? "Tisch wählen —" : "Zuerst Raum wählen"}</option>
                  {form.preferred_room_id && tablesForRoom.length === 0 && (<option value="" disabled>Keine freien Tische in diesem Raum</option>)}
                  {tablesForRoom.map((tbl) => (<option key={tbl.table_id} value={tbl.table_id}>{tbl.name} · {tbl.seats} Pl.</option>))}
                </select>
              </LinenField>
            </div>
          </ModalSection>

          <ModalSection title="Tisch-Kellner">
            <LinenField label="Zuständiger Kellner / Host (optional)">
              <select value={form.assigned_waiter_id} onChange={(e) => setField("assigned_waiter_id", e.target.value)} className="linen-input cursor-pointer" data-testid="manual-waiter">
                <option value="">später zuweisen —</option>
                {waiters.length === 0 && <option value="" disabled>Keine Kellner im Team</option>}
                {waiters.map((s) => (<option key={s.user_id} value={s.user_id}>{s.name} · {t(`staff.roles.${s.role}`)}</option>))}
              </select>
            </LinenField>
          </ModalSection>

          <ModalSection title="Anlass · Allergien · Notizen">
            <div className="space-y-6">
              <LinenField label={t("reservations.type")}>
                <select value={form.reservation_type} onChange={(e) => setField("reservation_type", e.target.value)} className="linen-input cursor-pointer" data-testid="manual-type">
                  {["dinner","romantic","birthday","business","family","event"].map((k) => (
                    <option key={k} value={k}>{t(`reservations.types.${k}`)}</option>
                  ))}
                </select>
              </LinenField>
              <LinenField label={t("common.allergies") + " (kommagetrennt)"}>
                <input value={form.allergies} onChange={(e) => setField("allergies", e.target.value)} className="linen-input" data-testid="manual-allergies" placeholder="Gluten, Laktose" />
              </LinenField>
              <LinenField label={t("common.notes")}>
                <textarea rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="linen-input resize-none" data-testid="manual-notes" placeholder="Tisch am Fenster, Geburtstagstorte…" />
              </LinenField>
            </div>
          </ModalSection>
        </form>

        {/* ===== Sticky footer — Submit always reachable ===== */}
        <footer className="shrink-0 px-8 sm:px-10 py-5 border-t border-white/[0.04] bg-[#16120E]">
          <div className="flex gap-4">
            <button type="button" onClick={onClose} className="btn-ghost text-[11px] tracking-[0.2em] uppercase flex-1" data-testid="manual-cancel">{t("common.cancel")}</button>
            <button
              type="submit"
              form="manual-reservation-form"
              disabled={submitting}
              className="btn-primary text-[11px] tracking-[0.2em] uppercase flex-1 disabled:opacity-50"
              data-testid="manual-submit"
            >
              {submitting ? "Speichere…" : t("reservations.manual_modal.submit")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ModalSection({ title, hint, children }) {
  return (
    <section>
      <div className="mb-5">
        <h4 className="font-editorial italic text-[1.35rem] leading-none text-[var(--text-primary)]">{title}</h4>
        {hint && <div className="text-[12px] text-[var(--text-muted)] mt-2 italic font-body">{hint}</div>}
      </div>
      {children}
    </section>
  );
}

function LinenField({ label, children }) {
  return (
    <div>
      <label className="text-[11px] text-[var(--text-muted)] mb-2 block uppercase tracking-[0.2em] font-medium">{label}</label>
      {children}
    </div>
  );
}
